import {useSelector, useDispatch} from "react-redux";
import React, {useState} from "react";
import {useUtilityData} from "../../utilities/contexts";
import withUtilities from "../../hooks/withUtilities";
import {Button} from "@headlessui/react";
import {postData} from "../../utilities/axios";
import {useRouter} from "next/router";
import {Spinner, useToast} from "@chakra-ui/react";
import {genericFail} from "../../utilities/notifications";
import {resetPms} from "../../redux/actions/pmsSearch";

const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const PaymentForm = () => {
  const pmsSearch = useSelector((state) => state.pmsSearch);
  const user = useSelector((state) => state.user.userDetails);
  const token = useSelector((state) => state.user.JWTToken);
  const utilities = useUtilityData((utilityData) => utilityData);
  const router = useRouter();
  const toast = useToast();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const startPayment = async () => {
    if(loading) {
      return;
    }

    setLoading(true);
    try {
      const savePMS = await postData(token, {
          formData: {
            ...pmsSearch,
            fullname: user.fullname,
            contactEmail: user.email,
          },
          contactEmail: user.email,
          type: 1,
        },
        `${process.env.NEXT_PUBLIC_NEW_PMS_URL}`);
      const data = await postData(
        token,
        {
          type: 1,
          pmsId: savePMS.pmsId,
          redirectUrl: `${process.env.NEXT_PUBLIC_HOST_URL}/accounts/user-account/dashboard`,
        },
        `${process.env.NEXT_PUBLIC_NEW_PAYMENT_URL}/pms/payment`,
      );
      setLoading(false);
      dispatch(resetPms());
      await router.push(data.url);
    }
    catch (e) {
      console.error(e);
      setLoading(false);
      toast(genericFail('Something went wrong when submitting your MatchPlus request, please try again'));
    }
  }

  return (
    <div className='w-full flex flex-col px-2 mt-6 gap-4'>
      <span className='text-brand-alt text-sm'>
        Please review your information below and then click &apos;Continue to payment&apos; to submit your request to Welldoing.
        One of our matching specialists will review your details and contact you with a match in the next 2-3 working days.
      </span>
      <div className='flex flex-col w-full text-sm'>
        <h2 className='text-xl font-bold w-full bg-brand px-4 py-2 text-white rounded-t'>
          Your details
        </h2>
        <div className='flex flex-col w-full gap-3 px-4 py-2 border-l border-r border-b rounded-b'>
          <div className='flex w-full justify-between'>
            <span>Name</span>
            <span className='text-brand-alt'>{user.fullname}</span>
          </div>
          <div className='flex w-full justify-between'>
            <span>Email</span>
            <span className='text-brand-alt'>{user.email}</span>
          </div>
          <div className='flex w-full justify-between'>
            <span>Contact Number</span>
            <span className='text-brand-alt'>{pmsSearch.contactNumber}</span>
          </div>
          <div className='flex w-full justify-between'>
            <span>Gender</span>
            <span
              className='text-brand-alt'>{pmsSearch.gender && pmsSearch.gender.length > 0 ? pmsSearch.gender[0].label : 'Other/Prefer not to say'}</span>
          </div>
          <div className='flex w-full justify-between'>
            <span>Age</span>
            <span className='text-brand-alt'>{pmsSearch.age}</span>
          </div>
          <div className='flex w-full justify-between'>
            <span>Sexuality</span>
            <span className='text-brand-alt'>{pmsSearch.sexuality ?? 'None given'}</span>
          </div>
          <div className='flex w-full justify-between'>
            <span>Your university</span>
            <span className='text-brand-alt'>{pmsSearch.university}</span>
          </div>
        </div>
      </div>
      <div className='flex flex-col w-full text-sm'>
        <h2 className='text-xl font-bold w-full bg-brand px-4 py-2 text-white rounded-t'>
          What&apos;s worrying you
        </h2>
        <div className='flex flex-col w-full gap-3 px-4 py-2 border-l border-r border-b rounded-b'>
          <span className='text-brand-alt'>{pmsSearch.symptoms}</span>
        </div>
      </div>
      <div className='flex flex-col w-full text-sm'>
        <h2 className='text-xl font-bold w-full bg-brand px-4 py-2 text-white rounded-t'>
          Your preferences
        </h2>
        <div className='flex flex-col w-full gap-3 px-4 py-2 border-l border-r border-b rounded-b'>
          <div className='flex w-full gap-8'>
            <span>Session availability</span>
            <div className='flex flex-1 flex-col text-brand-alt'>
              <div className='grid grid-cols-[90px_1fr]'>
                <div/>
                <div className='grid grid-cols-7 gap-1 pb-2'>
                  {days.map((day, i) => <p key={`weekDay_${i}`}
                                           className='text-xs text-center'>{day}</p>)}
                </div>
              </div>
              <div className='grid grid-cols-[90px_1fr]'>
                <div className='flex flex-col gap-2.5'>
                  {utilities.availability.map((label, i) => label && label !== '' ? (
                      <p className='text-xs py-1' key={`timeName_${i}`}>{label}</p>
                    ) : null
                  )}
                </div>
                <div className='grid grid-cols-7 gap-1'>
                  {pmsSearch.availability.map((availSet, id) => availSet.map((avail, day) => (
                    <div key={`day_${id}_${day}`}
                         className={`text-white text-sm ${avail ? 'bg-brand' : 'bg-brand-off'} opacity-1 hover:opacity-[0.7] rounded-[5px] flex justify-center items-center py-1 px-3 cursor-pointer`}>
                      <p className='absolute'>{avail ? '\u2714' : ''}</p>
                    </div>
                  )))}
                </div>
              </div>
            </div>
          </div>
          <div className='flex w-full justify-between'>
            <span>Therapists gender</span>
            <span
              className='text-brand-alt'>{pmsSearch.therapistGender && pmsSearch.therapistGender.length > 0 ? pmsSearch.therapistGender[0].label : 'No preference'}</span>
          </div>
        </div>
      </div>
      <div className='py-4 w-full flex justify-end'>
        <Button disabled={loading} onClick={startPayment}
                className={`flex items-center bg-brand text-white ${loading ? 'cursor-not-allowed opacity-[0.7]' : ' hover:bg-white hover:text-brand '} font-bold px-4 py-2 rounded-full transition`}>
          {loading ? 'Submitting...' : 'Continue to payment'}
          {loading && <Spinner size='xs' className='text-white ml-4' />}
        </Button>
      </div>
    </div>
  )
}

export default withUtilities(PaymentForm);
